import { StandardEvents } from '@shopify/events';

/* Exact selection context only; no network calls or form submission. */
(() => {
  if (window.tavilumeVideoRequestReady) return;
  window.tavilumeVideoRequestReady = true;
  let pendingSelection = null;
  let unresolvedPicker = null;

  const cleanLine = (value, limit = 1500) => String(value || '').replace(/[\r\n\t]/g, ' ').trim().slice(0, limit);
  const variantId = (value) => {
    const id = String(value || '').split('/').pop();
    return /^\d+$/.test(id) ? id : '';
  };
  const localProductPath = (value) => typeof value === 'string' && /^\/products\/[a-z0-9-]+(?:\?variant=\d+)?$/i.test(value) ? value : '';
  const mainPicker = () => document.querySelector('.product-details variant-picker[data-template-product-match="true"]');
  const selectedOptions = (picker) => Array.from(picker?.querySelectorAll('.variant-option') || []).map((group) => {
    const selected = group.querySelector('input:checked[data-option-name]') || group.querySelector('select')?.selectedOptions[0];
    if (selected) return { name: cleanLine(selected.dataset.optionName, 100), value: cleanLine(selected.value, 400) };
    const single = group.querySelector('[data-testid="variant-option-single-value"]');
    if (!single) return null;
    const name = Array.from(group.childNodes).filter((node) => node.nodeType === 3).map((node) => node.textContent).join(' ');
    return { name: cleanLine(name, 100), value: cleanLine(single.getAttribute('title') || single.textContent, 400) };
  }).filter((option) => option?.name && option?.value);

  const requestMessage = (context, isUpgrade) => {
    const lines = ['Hello Tavilume,', '', isUpgrade
      ? 'I would like a real product video and photos of the lamp I plan to customize.'
      : `I would like a real product video and photos of ${context.title}.`];
    if (isUpgrade) lines.push(`Upgrade: ${context.title}`);
    lines.push(`${isUpgrade ? 'Upgrade' : 'Product'} link: ${new URL(context.path, window.location.origin).href}`);
    if (context.options) lines.push(`Selected options: ${context.options}`);
    if (context.sku) lines.push(`SKU: ${context.sku}`);
    if (isUpgrade) lines.push('Lamp name or link:');
    lines.push('Additional questions:', '');
    return lines.join('\n');
  };

  const selectedContext = (fallback) => {
    const picker = mainPicker();
    const context = {
      title: cleanLine(picker?.dataset.productTitle || fallback.get('product'), 240),
      path: localProductPath(fallback.get('product_path')),
      options: cleanLine(fallback.get('options')),
      sku: cleanLine(fallback.get('sku'), 160),
    };
    if (!picker) return context;
    const basePath = localProductPath((picker.dataset.productUrl || window.location.pathname).split('?')[0]);
    if (!basePath) return context;
    context.path = basePath;
    context.sku = '';
    const choices = selectedOptions(picker);
    context.options = choices.map(({ name, value }) => `${name}: ${value}`).join(' | ');
    // An in-flight or failed choice must never reuse the previous variant or SKU.
    if (pendingSelection?.picker === picker || unresolvedPicker === picker) return context;
    try {
      const selected = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null');
      const id = variantId(selected?.id);
      if (id) {
        context.path = `${basePath}?variant=${id}`;
        context.sku = cleanLine(selected.sku, 160);
        if (!context.options && selected.title !== 'Default Title') context.options = cleanLine(selected.title);
      }
    } catch (_) {
      // Keep the chosen options rather than invent an exact variant when data is missing.
    }
    return context;
  };

  const syncTargets = () => {
    if (!localProductPath(window.location.pathname)) return;
    const links = Array.from(document.querySelectorAll('a[data-tvm-video-request]'));
    const first = links.find((link) => {
      const url = new URL(link.href, window.location.origin);
      return url.origin === window.location.origin && url.pathname === '/pages/contact';
    });
    if (!first) return;
    const firstUrl = new URL(first.href, window.location.origin);
    const context = selectedContext(firstUrl.searchParams);
    if (!context.title || !context.path) return;
    for (const link of links) {
      const target = new URL(link.href, window.location.origin);
      if (target.origin !== window.location.origin || target.pathname !== '/pages/contact') continue;
      target.searchParams.set('product', context.title);
      target.searchParams.set('product_path', context.path);
      for (const key of ['options', 'sku']) {
        if (context[key]) target.searchParams.set(key, context[key]);
        else target.searchParams.delete(key);
      }
      link.href = target.href;
    }
    const isUpgrade = firstUrl.searchParams.get('upgrade') === '1';
    const subject = `${isUpgrade ? 'Lamp video request for upgrade' : 'Real product video request'}: ${context.title}`;
    for (const email of document.querySelectorAll('a[data-tvm-video-email]')) {
      email.href = `mailto:yujielu593@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(requestMessage(context, isUpgrade))}`;
    }
  };

  const prefill = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('request') !== 'product-video') return;
    const context = {
      title: cleanLine(params.get('product'), 240),
      path: localProductPath(params.get('product_path')),
      options: cleanLine(params.get('options')),
      sku: cleanLine(params.get('sku'), 160),
    };
    if (!context.title || !context.path) return;
    const message = document.querySelector('textarea[name="contact[body]"]');
    if (!message || message.value.trim()) return;
    message.value = requestMessage(context, params.get('upgrade') === '1');
    const note = message.form?.querySelector('[data-tvm-video-request-note]');
    if (note) note.hidden = false;
    // Wait for layout, then honor the form anchor without stealing keyboard focus.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (window.location.hash !== '#TvmVideoContactForm') return;
      if (message.form?.contains(document.activeElement)) return;
      document.getElementById('TvmVideoContactForm')?.scrollIntoView({ block: 'start', behavior: 'auto' });
    }));
  };

  document.addEventListener(StandardEvents.productSelect, (event) => {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    if (!picker || picker !== mainPicker()) return;
    const selection = { picker };
    pendingSelection = selection;
    unresolvedPicker = picker;
    syncTargets();
    if (!event.promise || typeof event.promise.then !== 'function') return;
    event.promise.then((result) => {
      if (pendingSelection !== selection) return;
      pendingSelection = null;
      // Null resource means the selected combination has no resolved variant.
      unresolvedPicker = result?.detail?.resource ? null : picker;
      syncTargets();
    }).catch(() => {
      if (pendingSelection !== selection) return;
      pendingSelection = null;
      unresolvedPicker = picker;
      syncTargets();
    });
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest?.('a[data-tvm-video-request], a[data-tvm-video-email]')) syncTargets();
  });
  const boot = () => { syncTargets(); prefill(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
